import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Check, ChevronLeft, PhoneForwarded, PhoneMissed, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import type { ForwardingAction, ForwardingConnection, RuleStatus } from '../../types/forwarding';
import type { AnswerMode, RuleId } from '../../data/mobileForwarding';
import { getCarrier } from '../../data/mobileForwarding';
import { STATUS_LABEL, configuredRules, isOn, rulesForMode } from '../../utils/forwardingStatus';
import { dot, statusText } from './AnsweringStatusCard';
import { RemoveAllForwardingStep } from './RemoveAllForwardingStep';
interface AnsweringRulesSheetProps {
  open: boolean;
  connection: ForwardingConnection | null;
  onClose: () => void;
  onAction: (action: ForwardingAction) => void;
}
const RULE_TITLE: Record<RuleId, string> = {
  busy: 'When busy',
  unanswered: "When you don't answer",
  unreachable: 'When unreachable or off',
  all: 'Answer all calls'
};

/**
 * "Answering rules" — two exclusive modes. Choosing the other mode starts its
 * forwarding setup; nothing shows as Active until that setup is done.
 */
export function AnsweringRulesSheet({
  open,
  connection,
  onClose,
  onAction
}: AnsweringRulesSheetProps) {
  const {
    isLight
  } = useTheme();
  const [confirmMode, setConfirmMode] = useState<AnswerMode | null>(null);
  useEffect(() => {
    if (!open) setConfirmMode(null);
  }, [open]);
  const act = (a: ForwardingAction) => {
    onClose();
    onAction(a);
  };
  const isLandline = connection?.numberType === 'landline';
  const allStatus = connection?.rules.all;
  const allForwardingOn = allStatus === 'verified' || allStatus === 'reported';
  return <AnimatePresence data-id="element-469" data-mp="1hx3g2d9abo62">
      {open && connection && <div className="absolute inset-0 z-50 flex items-end" data-id="element-470" data-mp="140y6sipxi3wg">
          <motion.button type="button" aria-label="Close" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="absolute inset-0 bg-black/40" data-id="element-471" data-mp="181rwnhmxlsrz" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="rules-sheet-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} className={`relative max-h-[92%] w-full overflow-y-auto rounded-t-3xl px-6 pb-10 pt-5 ${isLight ? 'bg-white' : 'bg-[#141414]'}`} data-id="element-472" data-mp="30z5ik0s2lrp8">
            <div className={`mx-auto mb-4 h-1 w-10 rounded-full ${isLight ? 'bg-slate-200' : 'bg-white/15'}`} data-id="element-473" data-mp="3fn0fjtd42i84" />
            <div className="mb-5 flex items-start justify-between gap-3" data-id="element-474" data-mp="2896t449yrsk1">
              <div className="flex min-w-0 items-start gap-2" data-id="element-475" data-mp="287c86pyp00fy">
                {confirmMode && <button type="button" onClick={() => setConfirmMode(null)} aria-label="Back to answering rules" className={`-ml-1 mt-0.5 rounded-full p-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-476" data-mp="24a7qiqbpg7vr">
                    <ChevronLeft className="h-5 w-5" data-id="element-477" data-mp="soo3fa4rhwon" />
                  </button>}
                <div className="min-w-0" data-id="element-478" data-mp="xj22pnmjonyl">
                  <h2 id="rules-sheet-title" className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-479" data-mp="12f8w6gxr86zn">
                    {confirmMode === 'conditional' && allForwardingOn ? 'Switch to when you can’t answer' : confirmMode ? 'Switch answering mode?' : 'Answering rules'}
                  </h2>
                  <p className={`mt-0.5 truncate text-sm tabular-nums ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-480" data-mp="3d9eouz9p48gs">
                    {connection.phoneNumber} · {isLandline ? 'Landline' : 'Mobile'}
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className={`rounded-full p-2 ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-481" data-mp="2hyf6mc3iwl57">
                <X className="h-4 w-4" data-id="element-482" data-mp="2rx4edc1pgp7q" />
              </button>
            </div>

            <AnimatePresence mode="wait" initial={false} data-id="element-483" data-mp="247jxcyp44213">
              {confirmMode ? <motion.div key="confirm" initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -12
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-484" data-mp="1z908oqj4wc51">
                  {confirmMode === 'conditional' && allForwardingOn ? <RemoveAllForwardingStep connection={connection} onConfirmed={() => act({
              type: 'switchMode',
              mode: 'conditional',
              removedAll: true
            })} onCancel={() => setConfirmMode(null)} data-id="element-485" data-mp="1ghplajcmahhw" /> : <SwitchConfirm connection={connection} target={confirmMode} isLight={isLight} onContinue={() => act({
              type: 'switchMode',
              mode: confirmMode
            })} onCancel={() => setConfirmMode(null)} data-id="element-486" data-mp="3i74vk2ejw692" />}
                </motion.div> : <motion.div key="modes" initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 12
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="space-y-3" role="radiogroup" aria-label="Answering mode" data-id="element-487" data-mp="5pq447r8wqkg">
                  {/* Answer all calls */}
                  <ModeCard selected={connection.mode === 'all'} icon={PhoneForwarded} title="Answer all calls" desc="All incoming calls go directly to Vexu. Your phone won't ring." isLight={isLight} onSelect={() => connection.mode !== 'all' && setConfirmMode('all')} data-id="element-488" data-mp="vyz0mic17uaa">
                    {connection.mode === 'all' && <ul data-id="element-489" data-mp="d6wh8z809zwc">
                        <RuleRow rule="all" label="Call forwarding" status={connection.rules.all ?? 'needsSetup'} isLight={isLight} onAction={act} data-id="element-490" data-mp="dau2pri233je" />
                      </ul>}
                  </ModeCard>

                  {/* Only when I can't answer — landlines forward every call */}
                  {!isLandline && <ModeCard selected={connection.mode === 'conditional'} icon={PhoneMissed} title="Only when I can't answer" desc="Your phone rings first. Vexu picks up in the situations you choose." isLight={isLight} onSelect={() => connection.mode !== 'conditional' && setConfirmMode('conditional')} data-id="element-491" data-mp="263ijn5wtnrjo">
                      <ul aria-disabled={connection.mode === 'all'} className={`transition-opacity ${connection.mode === 'all' ? 'pointer-events-none opacity-50' : ''}`} data-id="element-492" data-mp="1r8u5z4h9pmsz">
                        {rulesForMode('conditional').map(rule => connection.mode === 'conditional' ? <RuleRow key={rule} rule={rule} label={RULE_TITLE[rule]} status={connection.rules[rule] ?? 'off'} isLight={isLight} onAction={act} data-id="element-493" data-mp="3ckm7k16k8cxm" /> : <SavedRow key={rule} label={RULE_TITLE[rule]} saved={connection.rules[rule] !== undefined} isLight={isLight} data-id="element-494" data-mp="6iwmfg4d45k9" />)}
                      </ul>
                      {connection.mode === 'all' && <p className={`px-4 pb-3 text-xs ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-495" data-mp="u8r2s7nw4c99">
                          Your choices are saved for when you switch back.
                        </p>}
                    </ModeCard>}

                  <button type="button" onClick={() => act({
              type: 'changeNumber'
            })} className={`mt-1 w-full py-2 text-sm font-medium ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-496" data-mp="2w1xb8356co93">
                    Connect a different number
                  </button>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function ModeCard({
  selected,
  icon: Icon,
  title,
  desc,
  isLight,
  onSelect,
  children
}: {
  selected: boolean;
  icon: React.ElementType;
  title: string;
  desc: string;
  isLight: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}) {
  return <div className={`overflow-hidden rounded-2xl border-2 transition-colors ${selected ? isLight ? 'border-vexu bg-white' : 'border-vexu bg-vexu/5' : isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-white/[0.03]'}`} data-id="element-497" data-mp="etvo7kphzyox">
      <button type="button" role="radio" aria-checked={selected} onClick={onSelect} className="flex w-full items-start gap-3 p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-vexu" data-id="element-498" data-mp="2d7x9odiaw375">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${selected ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/20 text-vexu' : isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-499" data-mp="1mkqqo6z7rdew">
          <Icon className="h-5 w-5" data-id="element-500" data-mp="2f71r9hmtlebp" />
        </span>
        <span className="min-w-0 flex-1 pr-2" data-id="element-501" data-mp="2k8he9f8k5uwq">
          <span className={`block text-[15px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-502" data-mp="2k64wb22svkjo">{title}</span>
          <span className={`mt-0.5 block text-[13px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-503" data-mp="2aa1k13e8i5j0">
            {desc}
          </span>
          {!selected && <span className={`mt-1.5 block text-xs font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-504" data-mp="uvdwf3ma7vwk">
              Switch to this mode
            </span>}
        </span>
        <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-vexu bg-vexu' : isLight ? 'border-slate-300' : 'border-white/20'}`} data-id="element-505" data-mp="r9iuoxlt5wxe">
          {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} data-id="element-506" data-mp="waq0ir4e4m1f" />}
        </span>
      </button>
      {children && <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-507" data-mp="8km71ckx22o5">{children}</div>}
    </div>;
}
function RuleRow({
  rule,
  label,
  status,
  isLight,
  onAction
}: {
  rule: RuleId;
  label: string;
  status: RuleStatus;
  isLight: boolean;
  onAction: (a: ForwardingAction) => void;
}) {
  // Only completed setups can be turned off; everything else offers setup.
  const on = status === 'verified' || status === 'reported';
  return <li className="flex list-none items-center gap-3 px-4 py-3" data-id="element-508" data-mp="3lxxsl6q5997x">
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${dot(status)}`} aria-hidden data-id="element-509" data-mp="3gzmhkso44so4" />
      <div className="min-w-0 flex-1" data-id="element-510" data-mp="ak8f1wrtxaaw">
        <p className={`text-sm font-semibold leading-snug ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-511" data-mp="aj6v12su5dh1">{label}</p>
        <p className={`text-xs font-medium ${statusText(status, isLight)}`} data-id="element-512" data-mp="einyxrsm62k3">{STATUS_LABEL[status]}</p>
      </div>
      <button type="button" onClick={() => onAction(on ? {
      type: 'turnOffRule',
      rule
    } : {
      type: 'setUpRule',
      rule
    })} className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${on ? isLight ? 'border border-slate-200 text-slate-600 hover:bg-slate-50' : 'border border-white/10 text-zinc-300 hover:bg-white/5' : 'bg-vexu text-white hover:bg-vexu-600'}`} data-id="element-513" data-mp="oatzhpwxlsaw">
        {on ? 'Turn off' : 'Set up'}
      </button>
    </li>;
}
function SavedRow({
  label,
  saved,
  isLight
}: {
  label: string;
  saved: boolean;
  isLight: boolean;
}) {
  return <li className="flex list-none items-center gap-3 px-4 py-3" data-id="element-514" data-mp="1gziv59f7x5f9">
      <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${saved ? 'border-vexu bg-vexu' : isLight ? 'border-slate-300' : 'border-white/20'}`} data-id="element-515" data-mp="1gxy25o8jxuji">
        {saved && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} data-id="element-516" data-mp="2wj8l3mjvpc20" />}
      </span>
      <p className={`min-w-0 flex-1 text-sm font-medium leading-snug ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-517" data-mp="2rqfgcyoeu3px">{label}</p>
      <span className={`text-xs ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-518" data-mp="2mqt5uwzyv1kt">{saved ? 'Saved' : 'Not chosen'}</span>
    </li>;
}

/** Explains what happens next and what to do with the old forwarding before switching. */
function SwitchConfirm({
  connection,
  target,
  isLight,
  onContinue,
  onCancel
}: {
  connection: ForwardingConnection;
  target: AnswerMode;
  isLight: boolean;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const toAll = target === 'all';
  const oldOn = configuredRules(connection).filter(r => isOn(r.status) && r.status !== 'needsSetup');
  const carrier = getCarrier(connection.setup?.carrierId ?? 'other');
  const viaSettings = connection.setup?.method === 'settings';
  const codes = Array.from(new Set(oldOn.map(r => carrier.codes[r.rule]?.deactivate).filter((c): c is string => Boolean(c))));
  const blanket = oldOn.some(r => carrier.codes[r.rule]?.deactivateIsBlanket);
  return <div data-id="element-519" data-mp="538akt0f3lz1">
      <p className={`text-[15px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-520" data-mp="52zwwywh0cms">
        {toAll ? 'Answer all calls' : "Only when I can't answer"}
      </p>
      <p className={`mt-1 text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-521" data-mp="3s8h3fitj90c9">
        {toAll ? "Every incoming call will go straight to Vexu and your phone won't ring." : 'Your phone will ring first, and Vexu will answer in the situations you saved.'}{' '}
        We'll guide you through setting it up with your carrier. It shows as
        Active only once setup is complete.
      </p>

      {oldOn.length > 0 && <div className={`mt-4 flex items-start gap-2.5 rounded-xl border p-3.5 ${isLight ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-amber-500/20 bg-amber-500/10 text-amber-200'}`} data-id="element-522" data-mp="38jyhrb7qk52b">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" data-id="element-523" data-mp="2kzpkwbiqsfql" />
          <div className="text-[13px] leading-relaxed" data-id="element-524" data-mp="2yyp8dfuiqol6">
            {toAll ? <>Your current “when I can't answer” forwarding stays on your line. Answering all calls takes priority, so it's fine to leave it — or remove it later.</> : <>
                <b data-id="element-525" data-mp="33vfmhxmrja3y">Answer all calls stays on until you remove it</b>, so every call keeps going to Vexu until then.
              </>}
            {viaSettings ? <> Turn off Call Forwarding in your phone settings{toAll ? ' if you want to tidy it up.' : ' before or after setup.'}</> : codes.length > 0 ? <>
                {' '}
                To remove it, dial{' '}
                {codes.map((c, i) => <span key={c} data-id="element-526" data-mp="2k9u4185mav0t" data-mpi={i}>
                    {i > 0 && ', '}
                    <code className="font-mono font-bold" data-id="element-527" data-mp="2k7hb5zybn08z">{c}</code>
                  </span>)}
                .{blanket && ' This removes all forwarding on your line, so do it before setting up the new mode.'}
              </> : <> Ask your carrier to remove the old forwarding if calls don't behave as expected.</>}
            {' '}If you used voicemail before Vexu, you may need to turn it back on.
          </div>
        </div>}

      <div className="mt-5 space-y-2" data-id="element-528" data-mp="380zflb4iz15b">
        <Button fullWidth onClick={onContinue} data-id="element-529" data-mp="3bzyhemuymhsj">
          Continue to setup
        </Button>
        <button type="button" onClick={onCancel} className={`w-full py-2 text-sm font-medium ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-530" data-mp="3h032yraq62pm">
          Keep current mode
        </button>
      </div>
    </div>;
}